import React, { useEffect, useState, useCallback, useRef } from 'react';
import { LANDING_SECTIONS } from '../../utils/landingSections';
import './LandingSideToc.css';

/**
 * Боковая навигация по якорям лендинга.
 * — Показывает текущий раздел и позволяет перейти к нужному;
 * — Тонкий индикатор прогресса всей страницы сверху;
 * — Скрывается на мобильных и на самом hero/футере;
 * — Уважает prefers-reduced-motion для скролла.
 */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const MOBILE_NAV_MQ = '(max-width: 1179px)';

const LandingSideToc = ({ sections = LANDING_SECTIONS, hideUntilOffset = 600 }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id || null);
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [isMobileNav, setIsMobileNav] = useState(false);
  const rafRef = useRef(null);
  const mobileTrackRef = useRef(null);

  const handleClick = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    el.scrollIntoView({ behavior, block: 'start' });
  }, []);

  const onKeyDown = useCallback((e, idx) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const last = sections.length - 1;
    let next = idx;
    if (e.key === 'ArrowUp')   next = Math.max(0, idx - 1);
    if (e.key === 'ArrowDown') next = Math.min(last, idx + 1);
    if (e.key === 'Home')      next = 0;
    if (e.key === 'End')       next = last;
    handleClick(sections[next].id);
  }, [sections, handleClick]);

  useEffect(() => {
    const update = () => {
      rafRef.current = null;
      const scrollY = window.scrollY || 0;
      const winH = window.innerHeight || 0;
      const docH = document.documentElement.scrollHeight || 1;
      const total = Math.max(1, docH - winH);
      const p = Math.min(1, Math.max(0, scrollY / total));
      setProgress(p);

      const farFromTop    = scrollY > hideUntilOffset;
      const farFromBottom = scrollY + winH < docH - winH * 0.5;
      setVisible(farFromTop && farFromBottom);

      const center = scrollY + winH / 2;
      let current = sections[0]?.id || null;
      for (const sec of sections) {
        const el = document.getElementById(sec.id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top + scrollY;
        if (top <= center) current = sec.id;
      }
      setActiveId(current);
    };

    const onScroll = () => {
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [sections, hideUntilOffset]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(MOBILE_NAV_MQ);
    const sync = () => setIsMobileNav(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const active = visible && isMobileNav;
    document.body.classList.toggle('lp-mobile-nav-active', active);
    return () => document.body.classList.remove('lp-mobile-nav-active');
  }, [visible, isMobileNav]);

  useEffect(() => {
    if (!isMobileNav || !visible || !activeId) return undefined;
    const track = mobileTrackRef.current;
    const btn = track?.querySelector(`[data-section-id="${activeId}"]`);
    if (!track || !btn || typeof btn.scrollIntoView !== 'function') return undefined;
    btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeId, isMobileNav, visible]);

  return (
    <>
      {/* Тонкий progress-bar сверху, всегда виден после первого скролла */}
      <div
        className={`lp-scroll-progress${progress > 0.01 ? ' lp-scroll-progress--visible' : ''}`}
        aria-hidden
      >
        <div className="lp-scroll-progress__bar" style={{ transform: `scaleX(${progress})` }} />
      </div>

      <nav
        className={`lp-side-toc${visible ? ' lp-side-toc--visible' : ''}`}
        aria-label="Навигация по разделам страницы"
      >
        <ul className="lp-side-toc__list">
          {sections.map((sec, idx) => {
            const isActive = activeId === sec.id;
            return (
              <li key={sec.id} className="lp-side-toc__item">
                <button
                  type="button"
                  className={`lp-side-toc__btn${isActive ? ' lp-side-toc__btn--active' : ''}`}
                  onClick={() => handleClick(sec.id)}
                  onKeyDown={(e) => onKeyDown(e, idx)}
                  aria-label={`Перейти к разделу: ${sec.label}`}
                  aria-current={isActive ? 'true' : undefined}
                  title={sec.label}
                >
                  <span className="lp-side-toc__dot" aria-hidden />
                  <span className="lp-side-toc__label">{sec.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <nav
        className={`lp-mobile-nav${visible ? ' lp-mobile-nav--visible' : ''}`}
        aria-label="Быстрая навигация по разделам"
      >
        <div className="lp-mobile-nav__track" ref={mobileTrackRef}>
          {sections.map((sec) => {
            const isActive = activeId === sec.id;
            return (
              <button
                key={sec.id}
                type="button"
                data-section-id={sec.id}
                className={`lp-mobile-nav__pill${isActive ? ' lp-mobile-nav__pill--active' : ''}`}
                onClick={() => handleClick(sec.id)}
                aria-current={isActive ? 'true' : undefined}
              >
                {sec.label}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default LandingSideToc;
